import { memo, useCallback, useMemo } from 'react';
import { SidebarBoxKey } from 'AdmissionsGuidance';
import { useTranslation } from 'react-i18next';
import { useContentProgress } from 'ContentProgress';
import { streamDashboardPath, type StreamForDashboard } from 'Lessons';
import { CourseItemBox } from 'CourseItemBox';
import { regionAwareImageUrlForFormat } from 'regionAwareImage';
import { animatePathChange } from 'RouteAnimation';
import { useStudentDashboardContent } from 'StudentDashboardContent';
import { BasicDesktopBox } from '../../BasicDesktopBox';

export function RecentCoursesBoxComponent() {
    const { lessonStreams } = useStudentDashboardContent();
    const { streamProgressByLocalePackId } = useContentProgress();

    const handlePress = useCallback((course: StreamForDashboard) => {
        animatePathChange(streamDashboardPath({ streamTitle: course.title, streamId: course.id }));
    }, []);

    // sort by last_progress_at, descending
    const recentCourses = useMemo(
        () =>
            lessonStreams
                .filter(stream => streamProgressByLocalePackId[stream.localePackId]?.startedAt)
                .sort((a, b) => {
                    const aProgress = streamProgressByLocalePackId[a.localePackId];
                    const bProgress = streamProgressByLocalePackId[b.localePackId];
                    return (bProgress?.startedAt ?? 0) - (aProgress?.startedAt ?? 0);
                })
                .slice(0, 3),
        [lessonStreams, streamProgressByLocalePackId],
    );

    if (recentCourses.length === 0) {
        return null;
    }

    return <RecentCoursesBoxPresentation recentCourses={recentCourses} onPress={handlePress} />;
}

function RecentCoursesBoxPresentation({
    recentCourses,
    onPress,
}: {
    recentCourses: StreamForDashboard[];
    onPress: (course: StreamForDashboard) => void;
}) {
    const { t } = useTranslation('back_royal', { keyPrefix: 'recentCoursesBox' });
    return (
        <BasicDesktopBox boxKey={SidebarBoxKey.recent_courses} headerText={t('heading')}>
            <div className="mt-1.5">
                {recentCourses.map(course => (
                    <div key={course.id} className="mb-1.5">
                        <CourseItemBox
                            showSubtitle={false}
                            courseId={course.id}
                            title={course.title}
                            src={regionAwareImageUrlForFormat(course.image, 'original') ?? ''}
                            onPress={() => onPress(course)}
                            progressInfo={null}
                            padding="2.5"
                            titleSize="sm"
                            hideEndIcon
                        />
                    </div>
                ))}
            </div>
        </BasicDesktopBox>
    );
}

export const RecentCoursesBox = memo(RecentCoursesBoxComponent) as unknown as typeof RecentCoursesBoxComponent;
